/**
 * BLOCKS section (§3.2 decision 7 + OCS §2.4): every block definition as
 * BLOCK_HEADER + children + ENDBLK, plus the *Model_Space/*Paper_Space
 * containers the format expects. Definition attdefs are re-emitted as real
 * ATTDEF records from the BlockDef attribute list (deduplicated); block
 * children are owned by their BLOCK_RECORD handle.
 */

import type { Entity } from '../../../../state/store';
import type { BlockDef } from '../../../../state/commandAuxStores';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { writeEntity } from './entities';
import { writeAttdef } from '../serializers/insertAttrib';

export interface BlockWritePlan {
    name: string;
    /** BLOCK_RECORD handle (pre-resolved, lives in ctx.blockRecords). */
    recordHandle: string;
    base: [number, number, number];
    /** Anonymous blocks (*D) carry flag 1. */
    anonymous?: boolean;
    children: Entity[];
}

/** Virtual ATTDEF entities for a definition (positions from its attdefs). */
export function attdefChildren(def: BlockDef): Entity[] {
    return def.attributes.map((attr, i) => {
        const src = def.entities.find((c) => c.type === 'attdef' && String((c.data as Record<string, unknown>).tag ?? '') === attr.tag);
        const pos = src ? ((src.data as Record<string, unknown>).position as number[]) ?? [0, 0, 0] : [0, 0, 0];
        return {
            id: `dxf:attdef:${def.name}:${i}`,
            type: 'attdef',
            name: `ATTDEF-${attr.tag}`,
            visible: true,
            locked: false,
            layer: src?.layer ?? '0',
            data: {
                tag: attr.tag, prompt: attr.prompt, defaultValue: attr.defaultValue,
                position: pos,
                height: src ? Number((src.data as Record<string, unknown>).height) || 0.25 : 0.25,
            },
        };
    });
}

/** Expand a BlockDef into a write plan (attdef members replaced by ATTDEFs). */
export function planFromBlockDef(def: BlockDef, recordHandle: string): BlockWritePlan {
    const children = [
        ...def.entities.filter((c) => c.type !== 'attdef'),
        ...attdefChildren(def),
    ];
    return { name: def.name, recordHandle, base: def.basePoint ?? [0, 0, 0], children };
}

function emitBlock(w: DxfStreamWriter, ctx: DxfWriteContext, plan: BlockWritePlan): void {
    const headerHandle = ctx.handles.entityHandle(`dxf:blockhdr:${plan.name}`);
    const endHandle = ctx.handles.entityHandle(`dxf:blockend:${plan.name}`);
    w.pair(0, 'BLOCK').pair(5, headerHandle).pair(330, plan.recordHandle);
    w.pair(100, 'AcDbEntity').pair(8, '0');
    w.pair(100, 'AcDbBlockBegin');
    w.pair(2, plan.name).pair(70, plan.anonymous ? 1 : 0);
    w.pair(10, plan.base[0] ?? 0).pair(20, plan.base[1] ?? 0).pair(30, plan.base[2] ?? 0);
    w.pair(3, plan.name).pair(1, ''); // name again + empty xref path
    plan.children.forEach((child) => {
        if (child.type === 'attdef') writeAttdef(w, ctx, child, plan.recordHandle);
        else writeEntity(w, ctx, child, plan.recordHandle);
    });
    w.pair(0, 'ENDBLK').pair(5, endHandle).pair(330, plan.recordHandle);
    w.pair(100, 'AcDbEntity').pair(8, '0');
    w.pair(100, 'AcDbBlockEnd');
}

export function writeBlocks(w: DxfStreamWriter, ctx: DxfWriteContext, plans: BlockWritePlan[]): void {
    w.pair(0, 'SECTION').pair(2, 'BLOCKS');
    plans.forEach((plan) => emitBlock(w, ctx, plan));
    w.pair(0, 'ENDSEC');
}
